import React from 'react';
import { Link } from 'react-router-dom';
import HomeNavbar from './HomeNavbar';

function TermsAndConditions() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0e27] to-[#0f172a]">
      <HomeNavbar />
      <div className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600 mb-3">
              Terms and Conditions
            </h1>
            <p className="text-gray-400 text-sm">Effective Date: 26 January 2026</p>
          </div>

          {/* Content */}
          <div className="bg-[#1a1f3a]/90 border border-blue-500/30 rounded-xl p-8 sm:p-12 shadow-xl backdrop-blur-sm">
            <div className="legal-content space-y-10 text-gray-300 leading-relaxed">
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
                <p className="mb-3">
                  Welcome to ConvrsAI by Market Wavegen, your trusted partner in revenue validation and qualification solutions.
                </p>
                <p className="mb-3">
                  These comprehensive Terms and Conditions (hereafter referred to as &quot;Terms&quot;) are designed to govern your use of our website, convrsai.com, and outline the terms and conditions under which you may purchase our services and products.
                </p>
                <p>
                  Please read these Terms carefully before accessing our website or making any purchases from ConvrsAI by Market Wavegen.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">2. Definitions</h2>
                <ul className="list-disc list-inside space-y-3 ml-2">
                  <li>
                    <strong className="text-gray-200">&quot;ConvrsAI by Market Wavegen&quot;</strong> refers to the legal entity that owns and operates the website convrsai.com, specializing in revenue validation and qualification platforms, AI-powered lead validation, automated calling, and related services.
                  </li>
                  <li>
                    <strong className="text-gray-200">&quot;Website&quot;</strong> refers to our online platform, convrsai.com, where we showcase our services and products.
                  </li>
                  <li>
                    <strong className="text-gray-200">&quot;Services&quot;</strong> encompasses the range of revenue validation and qualification solutions, AI-powered lead validation, firmographic verification, email and voice qualification, conversation intelligence, and related services provided by ConvrsAI by Market Wavegen.
                  </li>
                  <li>
                    <strong className="text-gray-200">&quot;Products&quot;</strong> refers to any physical or digital goods offered for sale on our website.
                  </li>
                  <li>
                    <strong className="text-gray-200">&quot;User&quot;</strong> or <strong className="text-gray-200">&quot;You&quot;</strong> denotes any individual or entity accessing, browsing, or making a purchase on our website.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">3. Use of Our Website</h2>
                <h3 className="text-lg font-semibold text-blue-300 mt-6 mb-2">3.1. Eligibility</h3>
                <p>
                  By accessing and using our website, you affirm that you are at least 18 years of age or are accessing the website under the supervision of a legal guardian. ConvrsAI by Market Wavegen is intended for use by individuals and businesses eligible under applicable law.
                </p>
                <h3 className="text-lg font-semibold text-blue-300 mt-6 mb-2">3.2. Account Registration</h3>
                <p>
                  Certain services on our website may require you to create an account. You are responsible for maintaining the confidentiality of your account information, and you are fully accountable for all activities conducted under your account.
                </p>
                <h3 className="text-lg font-semibold text-blue-300 mt-6 mb-2">3.3. Prohibited Activities</h3>
                <p>
                  You agree not to engage in any activities on our website that are illegal, unethical, or violate these Terms. Prohibited activities include, but are not limited to, unauthorized access to our systems, data scraping, and any attempts to disrupt the normal functioning of our website.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">4. Purchasing Services</h2>
                <h3 className="text-lg font-semibold text-blue-300 mt-6 mb-2">4.1. Service Descriptions</h3>
                <p>
                  ConvrsAI by Market Wavegen strives to provide accurate descriptions of our services. However, please note that service details, pricing, or availability may change over time. We do not guarantee that all information on our website will always be up-to-date or free from errors.
                </p>
                <h3 className="text-lg font-semibold text-blue-300 mt-6 mb-2">4.2. Payment</h3>
                <p>
                  Payment for our services is securely processed through our chosen payment gateway. By engaging our services, you agree to provide accurate payment information and authorize us to charge the specified amount for the service(s) you request.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">5. Refunds and Returns</h2>
                <h3 className="text-lg font-semibold text-blue-300 mt-6 mb-2">5.1. Refunds</h3>
                <p>
                  ConvrsAI by Market Wavegen refund policy is specific to the service provided. Detailed refund eligibility information can be found in the respective service description. We are committed to ensuring transparency in our refund policies.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">6. Intellectual Property</h2>
                <h3 className="text-lg font-semibold text-blue-300 mt-6 mb-2">6.1. Ownership</h3>
                <p>
                  All content displayed on our website, including but not limited to text, graphics, logos, images, and software, is the exclusive property of ConvrsAI by Market Wavegen and is safeguarded by intellectual property laws. You are expressly prohibited from using our content without obtaining prior written consent.
                </p>
                <h3 className="text-lg font-semibold text-blue-300 mt-6 mb-2">6.2. User-Generated Content</h3>
                <p>
                  If you choose to submit content to our website, such as reviews or comments, you grant ConvrsAI by Market Wavegen a non-exclusive, royalty-free, worldwide license to utilize, reproduce, and distribute that content as we see fit.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">7. Privacy Policy and Gmail Access</h2>
                <p>
                  Our Privacy Policy, accessible on our website, delineates how we collect, use, and protect your personal information. By using our website and services, you agree to abide by the terms of our{' '}
                  <Link to="/privacy-policy" className="text-blue-400 hover:text-blue-300 underline transition-colors">
                    Privacy Policy
                  </Link>
                  . When you connect your Gmail account, we request only the permission to send emails on your behalf; we do not request access to compose or manage drafts in your Gmail.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">8. Limitation of Liability</h2>
                <h3 className="text-lg font-semibold text-blue-300 mt-6 mb-2">8.1. No Warranties</h3>
                <p>
                  While ConvrsAI by Market Wavegen strives to offer accurate and reliable information, we make no warranties or representations regarding the accuracy, completeness, or reliability of the information on our website, nor the quality of our services and products.
                </p>
                <h3 className="text-lg font-semibold text-blue-300 mt-6 mb-2">8.2. Limitation of Liability</h3>
                <p>
                  To the fullest extent permissible under applicable law, ConvrsAI by Market Wavegen shall not be held liable for any direct, indirect, incidental, special, or consequential damages arising from or in connection with your use of our website or the purchase of our services and products.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">9. Governing Law and Dispute Resolution</h2>
                <h3 className="text-lg font-semibold text-blue-300 mt-6 mb-2">United States Location (Miami, Florida)</h3>
                <p>
                  These Terms are subject to the jurisdiction of the laws of the United States, with a particular emphasis on the state of Florida. Any disputes arising from or associated with these Terms will be resolved through arbitration, following the regulations outlined by the [Arbitration Association], unless contrary to the requirements of the applicable law.
                </p>
                <h3 className="text-lg font-semibold text-blue-300 mt-6 mb-2">Indian Location (Pune, Maharashtra, India)</h3>
                <p>
                  These Terms are governed by the laws of India, with a focus on the state of Maharashtra, where our location is based in Pune. Any disputes that may arise concerning these Terms will be settled through arbitration, in accordance with the rules established by the [Arbitration Association], unless it conflicts with the mandates of the applicable law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">10. Changes to Terms and Conditions</h2>
                <p>
                  ConvrsAI by Market Wavegen reserves the right to modify these Terms at any time. Any updates will be posted on our website. Continued use of our website and services after modifications constitute your acceptance of the revised Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">11. Contact Us</h2>
                <p>
                  Should you have any inquiries or concerns regarding these Terms and Conditions, please do not hesitate to contact our dedicated customer support team at{' '}
                  <a href="mailto:legal@convrsai.com" className="text-blue-400 hover:text-blue-300 underline transition-colors">
                    legal@convrsai.com
                  </a>
                  .
                </p>
                <p className="mt-4">
                  Thank you for choosing ConvrsAI by Market Wavegen. We greatly value your trust in our services and products and look forward to serving your revenue validation and qualification needs.
                </p>
              </section>
            </div>

            {/* Footer */}
            <footer className="mt-12 pt-8 border-t border-blue-500/20 flex flex-wrap gap-4 justify-between items-center">
              <nav aria-label="Footer navigation">
                <Link to="/privacy-policy" className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                  Privacy Policy
                </Link>
              </nav>
              <Link to="/" className="text-sm text-gray-400 hover:text-blue-400 transition-colors">
                Back to Home
              </Link>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsAndConditions;
